"use client";

import CommentComponent from "@/src/components/common/comment";
import Outlines from "@/src/components/common/outlines/outlines";
import PostKeywords from "@/src/components/common/keywords/keywords";
import ReactHtmlParser, { addHeadingIds } from "@/src/utils/html-parser";
import ReadingProgress from "@/src/components/main-site/posts/reading-progress/reading-progress";
import SocialIcons from "@/src/components/common/pages/social-icons/social-icons";
import CoverPlate from "@/src/components/common/broadsheet/cover-plate";
import Kicker from "@/src/components/common/broadsheet/kicker";
import MetaLine from "@/src/components/common/broadsheet/meta-line";
import Prose from "@/src/components/common/broadsheet/prose";
import {
  doubleRule,
  FONT_DISPLAY,
  FONT_TEXT,
  hairline,
  HEADING_SIZE,
  RAIL,
  SHEET_GUTTER,
  spineForJournal,
  stickyRailSx,
} from "@/src/components/common/broadsheet/tokens";
import { Box, Typography } from "@mui/material";
import { simpleDateFormatter } from "@/src/utils/dateFormatter";
import { isValidDate } from "@/src/utils/is-valid-date";

const WORDS_PER_MINUTE = 200;

const readingMinutes = (html: string) => {
  const text = (html || "").replace(/<[^>]*>/g, " ").trim();
  const words = text ? text.split(/\s+/).length : 0;
  return words ? Math.max(1, Math.round(words / WORDS_PER_MINUTE)) : 0;
};

export default function PostComponent({
  journal,
  post,
}: {
  journal: any;
  post: any;
}) {
  const modifiedContent = addHeadingIds(post?.body || "");
  const hasOutline = /<h[2-6][^>]*\sid=/i.test(modifiedContent);

  const cover =
    post?.image_url ||
    (journal.meta_data?.journal_default_cover_image
      ? `${process.env.NEXT_PUBLIC_SERVICE_URI_CDN_SERVE}${journal.meta_data.journal_default_cover_image}`
      : null);

  const published =
    post?.publish_date && isValidDate(new Date(post.publish_date))
      ? simpleDateFormatter(post.publish_date)
      : null;

  const minutes = readingMinutes(post?.body || "");

  return (
    <Box sx={{ px: SHEET_GUTTER, pb: { xs: 1.5, md: 2.5 } }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: hasOutline ? `${RAIL} 1fr ${RAIL}` : `1fr ${RAIL}`,
          },
          columnGap: 2.5,
          rowGap: 1.25,
          alignItems: "start",
          "& > *": { minWidth: 0 },
        }}
      >
        {hasOutline ? (
          <Box
            sx={{
              ...stickyRailSx,
              display: { xs: "none", lg: "block" },
              pt: 1.25,
              pr: 1.25,
              borderRight: "1px solid",
              borderColor: "custom.ruleHairline",
            }}
          >
            <Outlines
              content={modifiedContent}
              hasAbstract={!!post?.abstract}
              hasBody={!!modifiedContent}
            />
          </Box>
        ) : null}

        <Box component="article" sx={{ pt: { xs: 1, md: 1.5 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.25,
              pb: { xs: 0.75, md: 1 },
              ...doubleRule,
            }}
          >
            {cover ? (
              <CoverPlate
                src={cover}
                alt={post?.image_alt ?? ""}
                width={170}
                height={170}
                offset={5}
                spine={spineForJournal(journal)}
                priority
              />
            ) : null}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {post?.title ? (
                <Typography
                  component="h1"
                  sx={{
                    m: 0,
                    fontFamily: FONT_DISPLAY,
                    fontWeight: 700,
                    fontSize: HEADING_SIZE.page,
                    lineHeight: 1.08,
                    letterSpacing: "0.01em",
                    color: "custom.ink",
                  }}
                >
                  {post.title}
                </Typography>
              ) : null}
              {post?.subtitle ? (
                <Typography
                  component="p"
                  sx={{
                    m: 0,
                    mt: 0.25,
                    fontFamily: FONT_TEXT,
                    fontStyle: "italic",
                    fontSize: { xs: "0.9375rem", md: "1rem" },
                    lineHeight: 1.5,
                    color: "custom.inkMuted",
                  }}
                >
                  {post.subtitle}
                </Typography>
              ) : null}
              <MetaLine
                sx={{ mt: 0.375 }}
                items={[published, minutes ? `${minutes} min read` : null]}
              />
            </Box>
          </Box>

          {post?.abstract ? (
            <Box id="page-summary" sx={{ pt: 1, pb: 1, ...hairline }}>
              <Typography
                component="h2"
                sx={{
                  m: 0,
                  mb: 0.375,
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 700,
                  fontSize: HEADING_SIZE.section,
                  lineHeight: 1.15,
                  color: "custom.link",
                }}
              >
                Summary
              </Typography>
              <Prose>{ReactHtmlParser(post.abstract)}</Prose>
            </Box>
          ) : null}

          {modifiedContent ? (
            <Prose id="page-content-body" sx={{ pt: 1 }}>
              {ReactHtmlParser(modifiedContent)}
            </Prose>
          ) : null}

          {post?.keywords ? (
            <PostKeywords
              journalPath={journal.path}
              isMainSite={journal.is_main_site}
              keywords={post.keywords}
            />
          ) : null}

          <Box sx={{ mt: 1.5 }}>
            <CommentComponent
              journalSlug={journal.slug}
              entityId={post?.id}
              entity="post"
            />
          </Box>
        </Box>

        <Box
          sx={{
            ...stickyRailSx,
            display: { xs: "none", lg: "block" },
            pt: 1.5,
            pl: 1.25,
            borderLeft: "1px solid",
            borderColor: "custom.ruleHairline",
          }}
        >
          <ReadingProgress contentText={post?.body || ""} />
          <Kicker sx={{ mb: 0.25 }}>Share</Kicker>
          <SocialIcons direction="column" />
        </Box>
      </Box>
    </Box>
  );
}
